import { mapGetters } from 'vuex';
import uuid from 'uuid/v1'
import moment from 'moment'
import { defaultAmountShipping, flatDiscount200, flatDiscount300, federalExciseTaxRate } from '@/config'

export default {
  data: function () {
    return {
      flatDiscount200,
      flatDiscount300,
    }
  },
  computed: {
    ...mapGetters([
      'order',
      'customer',
      'items',
      'priceDiscountGroupsToPriceDiscountLevels'
    ]),
    exists() {
      return (this.order.id) ? true : false
    },
    isEditable() {
      return ! this.isCanceled && ! this.isSubmitted && ! this.$wait.any
    },
    isOrderItemEditable() {
      return this.isEditable && this.hasPurchaseOrderNumber && this.hasNeededDate && this.hasBillingAddress && this.hasShippingAddress
    },
    isSavable() {
      return this.isEditable && this.isDirty && this.hasOrderItems && ! this.$wait.any
    },
    isSubmittable() {
      return this.isEditable && this.exists && ! this.isDirty && ! this.$wait.any
    },
    isCancelable() {
      return ! this.isCanceled && this.exists && ! this.isDirty && ! this.$wait.any
    },
    isCanceled() {
      return ['CANCELED'].includes(this.order.status)
    },
    isDraft() {
      return ['DRAFT'].includes(this.order.status)
    },
    isSubmitted() {
      return ['PENDING_IMPORT', 'IMPORTED'].includes(this.order.status)
    },
    hasPurchaseOrderNumber() {
      return this.order.purchase_order_number
    },
    hasNeededDate() {
      return this.order.needed_date
    },
    hasBillingAddress() {
      return this.order.billing_address_id
    },
    hasShippingAddress() {
      return this.order.shipping_address_id
    },
    hasOrderItems: function () {
      return this.order.hasOwnProperty('orderItems') && this.order.orderItems.length
    },
    isOrderFederalExciseTaxable () {
      return ['US'].includes(this.order.shipping_country) && ! this.order.is_federal_excise_tax_exempt;
    }
  },
  methods: {
    initOrder() {
      return {
        id: null,
        customer_id: this.customer.id,
        billing_address_id: null, // this.customer.defaultBillingAddress.id,
        shipping_address_id: null, // this.customer.defaultShippingAddress.id,
        last_autosave_date: null,
        status: 'DRAFT',
        amount_subtotal: 0.00,
        amount_retail_subtotal: 0.00,
        amount_total: 0.00,
        is_federal_excise_tax_exempt: this.customer.is_federal_excise_tax_exempt,
        federal_excise_tax_rate: 0.00,
        amount_federal_excise_tax: 0.00,
        is_sales_tax_exempt: this.customer.is_sales_tax_exempt,
        sales_tax_rate: 0.00,
        amount_sales_tax: 0.00,
        amount_tax: 0.00,
        amount_shipping: 0.00,
        price_discount_level_id: this.customer.price_discount_level_id,
        needed_date: null/* moment().add(90, 'day').format() */, // TODO: Use today's date if we're currently in the proactive discount period
        purchase_order_number: '',
        notes: '',
        billingAddress: null,
        shippingAddress: null,
        orderItems: []
      }
    },
    initOrderItem(item, qty) {
      let priceDiscountGroupToPriceDiscountLevel = this.getPriceDiscountGroupToPriceDiscountLevelForItem(item)
      let price = this.calculateItemPrice(item, priceDiscountGroupToPriceDiscountLevel)
      return {
        id: null,
        'uuid': uuid(),
        'order_id': this.order.id || null,
        'item_id': item.id,
        'customer_id': this.customer.id,
        'item_code': item.item_code,
        'item_name': item.item_name,
        'price_discount_rate': priceDiscountGroupToPriceDiscountLevel.rate,
        'price': price,
        'retail_price': item.price,
        'optics_price': item.optics_price,
        'is_sales_taxable': item.is_sales_taxable,
        'is_federal_excise_taxable': item.is_federal_excise_taxable,
        'federal_excise_tax': this.calculateItemFederalExciseTax(item, price, priceDiscountGroupToPriceDiscountLevel),
        'is_federal_firearms_license_required': item.is_federal_firearms_license_required,
        'quantity': qty,
        'subtotal': this.calculateItemSubtotal(price, qty),
        'sales_tax_subtotal': this.calculateItemSalesTaxSubtotal(item, qty),
        'federal_excise_tax_subtotal': this.calculateItemFederalExciseTaxSubtotal(item, qty, priceDiscountGroupToPriceDiscountLevel),
      }
    },
    reinitOrderItems() {
      let orderItems = []
      this.order.orderItems.forEach((orderItem) => {
        let item = this.getItemFromOrderItem(orderItem)
        orderItem = this.initOrderItem(item, orderItem.quantity)
        orderItems.push(orderItem)
      })
      this.order.orderItems = orderItems
    },
    captureBillingAddress () {
      this.$set(this.order, 'billing_business_name', this.order.billingAddress.business_name)
      this.$set(this.order, 'billing_contact_name', this.order.billingAddress.contact_name)
      this.$set(this.order, 'billing_street_address', this.order.billingAddress.street_address)
      this.$set(this.order, 'billing_street_address_2', this.order.billingAddress.street_address_2)
      this.$set(this.order, 'billing_city', this.order.billingAddress.city)
      this.$set(this.order, 'billing_state', this.order.billingAddress.state)
      this.$set(this.order, 'billing_postal_code', this.order.billingAddress.postal_code)
      this.$set(this.order, 'billing_country', this.order.billingAddress.country)
      this.$set(this.order, 'billing_residential_status', this.order.billingAddress.residential_status)
    },
    captureShippingAddress () {      
      this.$set(this.order, 'shipping_business_name', this.order.shippingAddress.business_name)
      this.$set(this.order, 'shipping_contact_name', this.order.shippingAddress.contact_name)
      this.$set(this.order, 'shipping_street_address', this.order.shippingAddress.street_address)
      this.$set(this.order, 'shipping_street_address_2', this.order.shippingAddress.street_address_2)
      this.$set(this.order, 'shipping_city', this.order.shippingAddress.city)
      this.$set(this.order, 'shipping_state', this.order.shippingAddress.state)
      this.$set(this.order, 'shipping_postal_code', this.order.shippingAddress.postal_code)
      this.$set(this.order, 'shipping_country', this.order.shippingAddress.country)
      this.$set(this.order, 'shipping_residential_status', this.order.shippingAddress.residential_status)
    },
    isItemFederalExciseTaxable (item) {
      return this.isOrderFederalExciseTaxable && item.is_federal_excise_taxable;
    },
    calculateItemPrice(item, priceDiscountGroupToPriceDiscountLevel) {
      if (this.flatDiscount200.includes(item.item_code)) {
        return (Math.ceil((item.price - 200) * 100) / 100).toFixed(2)
      } else if (this.flatDiscount300.includes(item.item_code)) {
        return (Math.ceil((item.price - 300) * 100) / 100).toFixed(2)
      } else {
        return parseFloat((Math.round((item.price - (item.price * priceDiscountGroupToPriceDiscountLevel.rate)) * 100) / 100).toFixed(2))
      }
    },
    calculateItemSubtotal (price, quantity) {
      return price * quantity
    },
    calculateItemSalesTaxSubtotal (item, quantity) {
      return (this.order.shipping_state !== 'PA' || ! item.is_sales_taxable || this.order.is_sales_tax_exempt) ? 0.00 : parseFloat((Math.ceil((this.calculateItemSubtotal(item, quantity) * .06) * 100) / 100).toFixed(2))
    },
    calculateItemFederalExciseTaxSubtotal (item, quantity, priceDiscountGroupToPriceDiscountLevel) {
      return (! this.isItemFederalExciseTaxable(item)) ? 0.00 : parseFloat((Math.ceil((this.calculateItemFederalExciseTax(item, this.calculateItemPrice(item, priceDiscountGroupToPriceDiscountLevel), priceDiscountGroupToPriceDiscountLevel) * quantity) * 100) / 100).toFixed(2))
    },
    calculateItemFederalExciseTax (item, price, priceDiscountGroupToPriceDiscountLevel) {
      if (! this.isItemFederalExciseTaxable(item)) {
        return 0.00;
      }
      let discountedOpticsPrice = (Math.ceil((item.optics_price - (item.optics_price * priceDiscountGroupToPriceDiscountLevel.rate)) * 100) / 100).toFixed(4)
      let priceLessDiscountedOptics = (Math.ceil((price - discountedOpticsPrice) * 100) / 100).toFixed(4)
      let priceLessDiscountedOpticsLessCost = (Math.ceil((priceLessDiscountedOptics - item.cost) * 100) / 100).toFixed(4)
      let priceLessDiscountedOpticsLessCostTimesFederalExciseTaxRate = (Math.ceil((priceLessDiscountedOpticsLessCost * federalExciseTaxRate) * 100) / 100).toFixed(4)
      let priceLessDiscountedOpticsLessCostTimesFederalExciseTaxRateTimesFactor = (Math.ceil((priceLessDiscountedOpticsLessCostTimesFederalExciseTaxRate * 100) * 100) / 100).toFixed(4)
      let federalExciseTax = (Math.ceil((priceLessDiscountedOpticsLessCostTimesFederalExciseTaxRateTimesFactor / 100) * 100) / 100).toFixed(2)
      return parseFloat(federalExciseTax)
    },
    getItemFromOrderItem (orderItem) {
      return this.items.find(item => item.id === orderItem.item_id) || null
    },
    getPriceDiscountGroupToPriceDiscountLevelByIds(priceDiscountGroupId, priceDiscountLevelId) {
      return this.priceDiscountGroupsToPriceDiscountLevels.find(priceDiscountGroupToPriceDiscountLevel => priceDiscountGroupToPriceDiscountLevel.price_discount_group_id === priceDiscountGroupId && priceDiscountGroupToPriceDiscountLevel.price_discount_level_id === priceDiscountLevelId) || null
    },
    getPriceDiscountGroupToPriceDiscountLevelForItem(item) {
      return this.getPriceDiscountGroupToPriceDiscountLevelByIds(item.price_discount_group_id, this.order.price_discount_level_id)
    },
    applyOrderCalculations () {
      this.order.item_count = 0
      this.order.subtotal = 0.00
      this.order.shipping = 0.00
      this.order.retail_subtotal = 0.00
      this.order.sales_tax = 0.00
      this.order.federal_excise_tax = 0.00
      this.order.tax = 0.00
      this.order.total = 0.00

      this.order.shipping = (this.isShippingInternational) ? 0.00 : defaultAmountShipping

      this.order.orderItems.forEach((orderItem) => {
        this.order.item_count = this.order.item_count + orderItem.quantity
        this.order.subtotal = this.order.subtotal + orderItem.subtotal
        this.order.retail_subtotal = this.order.retail_subtotal + orderItem.retail_subtotal
        this.order.sales_tax = this.order.sales_tax + orderItem.sales_tax_subtotal
        this.order.federal_excise_tax = this.order.federal_excise_tax + orderItem.federal_excise_tax_subtotal
      })
      this.order.tax = this.order.sales_tax + this.order.federal_excise_tax
      this.order.total = this.order.subtotal + this.order.tax + this.order.shipping
    }
  }
}